/*
 * assets/ba-compare.js
 * WHAT: State engine + DOM controller for the "Add to compare" feature.
 *       Reads/writes the URL query param `?compare=h1,h2,h3,h4`, syncs the
 *       `[data-compare-toggle]` pills on every product tile, and renders
 *       the sticky bar (snippets/ba-compare-bar.liquid).
 * WHY:  Single source of truth = the URL. CLAUDE.md rule 5 forbids
 *       localStorage/sessionStorage; the URL is the only place selection
 *       persists. JS is purely reflective — render(state) on every change.
 * WHEN: 2026-06-09.
 *
 * Constraints:
 *   - Vanilla ES6+ only. No frameworks.
 *   - Cap at 4 selected handles.
 *   - history.replaceState (not pushState) so + Compare clicks are NOT
 *     back-button-trappable; we just rewrite the current URL.
 *   - Preserve any other query params (?filter.v.X, ?page=, etc.) on every
 *     URL write.
 *   - WCAG AA: aria-pressed on pills, aria-disabled when cap reached,
 *     proper focus management on chip-remove.
 *
 * URL encoding decision:
 *   The URL stays handle-only — `?compare=earbuds-pump-x,earbuds-zone-2`.
 *   Titles + images are NOT encoded in the URL because:
 *     1. URLs stay short, shareable, and don't leak marketing copy.
 *     2. Shopify product titles can contain commas, ampersands, and
 *        diacritics which would need escape-on-encode + decode-on-read
 *        and still look ugly when pasted.
 *     3. The full /pages/compare page does a Liquid lookup on each handle
 *        anyway, so a bookmarked/shared URL works correctly without any
 *        client-side title cache.
 *   We hydrate display metadata (title/image/price) from `data-product-*`
 *   attributes on the in-page tile pills into a Map cached for the lifetime
 *   of the page. If a handle is in the URL but the matching tile is not on
 *   this page (e.g. you came back to /collections/all from /collections/
 *   earbuds with 2 items selected), we render the chip with a humanised
 *   fallback derived from the handle (`earbuds-pump-x` → "Earbuds Pump X").
 *   The full comparison page corrects this on landing.
 */

(function () {
  'use strict';

  const QS_PARAM = 'compare';
  const MAX_HANDLES = 4;
  const DEBUG = new URLSearchParams(window.location.search).get('compare-debug') === '1';

  // In-memory metadata cache: handle -> { title, image, price }.
  // Populated from tile data-attributes as we see them.
  const metaCache = new Map();

  function log(...args) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.debug('[ba-compare]', ...args);
    }
  }

  /* ------------------------------------------------------------ *
   *  URL <-> state
   * ------------------------------------------------------------ */

  function readHandlesFromURL() {
    const raw = new URL(window.location).searchParams.get(QS_PARAM) || '';
    const handles = raw
      .split(',')
      .map(h => h.trim())
      .filter(Boolean);
    // Dedupe (preserve order) + cap.
    const seen = new Set();
    const deduped = [];
    for (const h of handles) {
      if (!seen.has(h)) {
        seen.add(h);
        deduped.push(h);
      }
      if (deduped.length >= MAX_HANDLES) break;
    }
    return deduped;
  }

  function writeHandlesToURL(handles) {
    const url = new URL(window.location);
    if (handles.length === 0) {
      url.searchParams.delete(QS_PARAM);
    } else {
      url.searchParams.set(QS_PARAM, handles.join(','));
    }
    // replaceState — we don't want each pill click to be a history entry.
    window.history.replaceState({}, '', url.toString());
    log('url ->', url.search || '(no query)');
  }

  /* ------------------------------------------------------------ *
   *  Tile pills
   * ------------------------------------------------------------ */

  function getAllToggles() {
    return document.querySelectorAll('[data-compare-toggle]');
  }

  function cacheToggleMeta(btn) {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    // Only write the first time — later renders of the same tile shouldn't
    // overwrite (every product-tile render emits the same data anyway).
    if (!metaCache.has(handle)) {
      metaCache.set(handle, {
        title: btn.dataset.productTitle || humaniseHandle(handle),
        image: btn.dataset.productImage || '',
        price: btn.dataset.productPrice || ''
      });
    }
  }

  function humaniseHandle(handle) {
    return handle
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  function syncToggleStates(handles) {
    const atCap = handles.length >= MAX_HANDLES;
    const selected = new Set(handles);
    for (const btn of getAllToggles()) {
      const handle = btn.dataset.productHandle;
      const isOn = selected.has(handle);
      btn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
      if (!isOn && atCap) {
        btn.setAttribute('aria-disabled', 'true');
        btn.disabled = true;
      } else {
        btn.removeAttribute('aria-disabled');
        btn.disabled = false;
      }
    }
  }

  /* ------------------------------------------------------------ *
   *  Bar rendering
   * ------------------------------------------------------------ */

  function getBar() {
    return document.getElementById('ba-compare-bar');
  }

  function renderBar(handles) {
    const bar = getBar();
    if (!bar) return;

    // Visibility — pure derivation from selection length.
    if (handles.length === 0) {
      bar.setAttribute('hidden', '');
      bar.setAttribute('data-active', 'false');
    } else {
      bar.removeAttribute('hidden');
      bar.setAttribute('data-active', 'true');
    }

    // Count badges (both variants use [data-compare-count]).
    bar.querySelectorAll('[data-compare-count]').forEach(el => {
      el.textContent = String(handles.length);
    });

    // CTA href — keep in sync so the anchor's default click navigates correctly.
    // Spec: /pages/compare?products=X,Y,Z
    bar.querySelectorAll('[data-compare-cta]').forEach(a => {
      if (handles.length === 0) {
        a.setAttribute('href', '/pages/compare');
      } else {
        a.setAttribute('href', '/pages/compare?products=' + handles.join(','));
      }
    });

    renderChipList(bar, handles);
    renderPreviewList(bar, handles);
  }

  function renderChipList(bar, handles) {
    const list = bar.querySelector('[data-compare-chip-list]');
    if (!list) return;
    list.innerHTML = '';
    for (const handle of handles) {
      const meta = metaCache.get(handle) || { title: humaniseHandle(handle), image: '' };
      const li = document.createElement('li');
      li.className = 'ba-compare-bar__chip';
      li.dataset.handle = handle;

      if (meta.image) {
        const img = document.createElement('img');
        img.src = meta.image;
        img.alt = '';            // decorative — adjacent text label carries the name
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 24;
        img.height = 24;
        li.appendChild(img);
      }

      const name = document.createElement('span');
      name.className = 'ba-compare-bar__chip-name';
      name.textContent = meta.title;
      li.appendChild(name);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ba-compare-bar__chip-remove';
      remove.setAttribute('aria-label', 'Remove ' + meta.title + ' from comparison');
      remove.dataset.compareRemove = handle;
      remove.textContent = '×'; // ×
      li.appendChild(remove);

      list.appendChild(li);
    }
  }

  function renderPreviewList(bar, handles) {
    const list = bar.querySelector('[data-compare-preview-list]');
    if (!list) return;
    list.innerHTML = '';
    for (const handle of handles) {
      const meta = metaCache.get(handle) || { title: humaniseHandle(handle), image: '', price: '' };
      const li = document.createElement('li');
      li.className = 'ba-compare-bar__preview-item';
      li.dataset.handle = handle;

      if (meta.image) {
        const img = document.createElement('img');
        img.src = meta.image;
        img.alt = '';            // decorative — text below carries name
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 56;
        img.height = 56;
        li.appendChild(img);
      }

      const metaBox = document.createElement('div');
      metaBox.className = 'ba-compare-bar__preview-meta';

      const title = document.createElement('span');
      title.className = 'ba-compare-bar__preview-title';
      title.textContent = meta.title;
      metaBox.appendChild(title);

      if (meta.price) {
        const price = document.createElement('span');
        price.className = 'ba-compare-bar__preview-price';
        price.textContent = meta.price;
        metaBox.appendChild(price);
      }

      li.appendChild(metaBox);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ba-compare-bar__preview-remove';
      remove.setAttribute('aria-label', 'Remove ' + meta.title + ' from comparison');
      remove.dataset.compareRemove = handle;
      remove.textContent = '×';
      li.appendChild(remove);

      list.appendChild(li);
    }
  }

  /* ------------------------------------------------------------ *
   *  Event handlers
   * ------------------------------------------------------------ */

  function handleToggleClick(e) {
    const btn = e.target.closest('[data-compare-toggle]');
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') {
      // Cap reached. Silent no-op — visual state already communicates it.
      e.preventDefault();
      return;
    }
    const handle = btn.dataset.productHandle;
    if (!handle) return;

    cacheToggleMeta(btn);

    let handles = readHandlesFromURL();
    const idx = handles.indexOf(handle);
    if (idx >= 0) {
      // Already selected -> remove.
      handles.splice(idx, 1);
    } else {
      // Not selected -> add, if under cap.
      if (handles.length >= MAX_HANDLES) {
        log('cap reached, ignoring add for', handle);
        return;
      }
      handles.push(handle);
    }

    writeHandlesToURL(handles);
    syncToggleStates(handles);
    renderBar(handles);
    log('toggle', handle, '->', handles);
  }

  function handleClear() {
    writeHandlesToURL([]);
    syncToggleStates([]);
    renderBar([]);
    log('cleared');
  }

  function handleRemoveFromBar(e) {
    const btn = e.target.closest('[data-compare-remove]');
    if (!btn) return;
    const handle = btn.dataset.compareRemove;
    if (!handle) return;

    let handles = readHandlesFromURL();
    handles = handles.filter(h => h !== handle);
    writeHandlesToURL(handles);
    syncToggleStates(handles);
    renderBar(handles);

    // Return focus to the matching tile pill if it exists on the page,
    // else to the bar's CTA. Avoids stranded focus when the chip vanishes.
    const tilePill = document.querySelector(
      '[data-compare-toggle][data-product-handle="' + cssEscape(handle) + '"]'
    );
    if (tilePill) {
      tilePill.focus({ preventScroll: true });
    } else {
      const cta = document.querySelector('#ba-compare-bar [data-compare-cta]');
      if (cta) cta.focus({ preventScroll: true });
    }
    log('removed from bar', handle, '->', handles);
  }

  // Minimal CSS.escape polyfill — only used to safely embed product handles
  // into a querySelector. Shopify handles are already lowercased + dashed
  // so this is conservative but cheap.
  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return s.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  /* ------------------------------------------------------------ *
   *  Init
   * ------------------------------------------------------------ */

  function init() {
    // 1. Hydrate metaCache from every tile pill currently on the page.
    for (const btn of getAllToggles()) {
      cacheToggleMeta(btn);
    }

    const handles = readHandlesFromURL();
    log('init handles', handles);

    // 2. Sync pill states + render bar.
    syncToggleStates(handles);
    renderBar(handles);

    // 3. Delegate listeners — robust against pagination/AJAX tile re-render.
    document.addEventListener('click', function (e) {
      // Order matters: bar-remove must be checked before toggle (a remove
      // button is NEVER a toggle, but defensive ordering keeps the intent
      // obvious).
      if (e.target.closest('[data-compare-remove]')) {
        handleRemoveFromBar(e);
        return;
      }
      if (e.target.closest('[data-compare-clear]')) {
        handleClear();
        return;
      }
      if (e.target.closest('[data-compare-toggle]')) {
        handleToggleClick(e);
        return;
      }
      // CTA anchor click is left to default-navigate — its href was
      // rewritten in renderBar() above.
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Shopify's script_tag emits a defer/async-style include; in case it
    // loads after DOMContentLoaded has already fired (script_tag uses
    // async), fall through and init immediately.
    init();
  }
})();
